import { d } from "./tickMath";
import {
  getCoinAmountFromLiquidity,
  getShareLiquidityByAmount
} from "./utils";
import BN from "bn.js";
import { TickMath } from "@mmt-finance/clmm-sdk";
import { LiquidityInput, Vault } from "../types";
import Decimal from "decimal.js";
import { MathUtil } from "./math";

export async function estLiquidityAmountFromFtAmount(params: {
  input_ft_amount: string;
  slippage: number;
  vault: Vault;
}) {
  const { input_ft_amount, slippage, vault } = params
  const pool = vault.pool;
  // Extract position details
  const { position } = vault
  const lowerTick = position.tick_lower_index
  const upperTick = position.tick_upper_index
  const lowerSqrtPrice = TickMath.tickIndexToSqrtPriceX64(lowerTick)
  const upperSqrtPrice = TickMath.tickIndexToSqrtPriceX64(upperTick)

  const lpTokenAmount = new BN(input_ft_amount)
  const liquidity = getShareLiquidityByAmount(vault, lpTokenAmount.toString())
  const curSqrtPrice = new BN(pool.currentSqrtPrice)

  const coinAmounts = getCoinAmountFromLiquidity(new BN(liquidity), curSqrtPrice, lowerSqrtPrice, upperSqrtPrice, false)

  const minAmountA = d(coinAmounts.coin_amount_a.toString()).mul(d(1 - slippage))
  const minAmountB = d(coinAmounts.coin_amount_b.toString()).mul(d(1 - slippage))

  return {
    amount_a: coinAmounts.coin_amount_a.toString(),
    amount_b: coinAmounts.coin_amount_b.toString(),
    amount_limit_a: minAmountA.toFixed(0),
    amount_limit_b: minAmountB.toFixed(0),
  }
}

export function estLiquidityAndCoinAmountFromOneAmounts(
  lowerTick: number,
  upperTick: number,
  coinAmount: BN,
  isCoinA: boolean,
  roundUp: boolean,
  slippage: number,
  curSqrtPrice: BN
): LiquidityInput {
  const current_tick = TickMath.sqrtPriceX64ToTickIndex(curSqrtPrice)
  const lower_sqrt_price = TickMath.tickIndexToSqrtPriceX64(lowerTick)
  const upper_sqrt_price = TickMath.tickIndexToSqrtPriceX64(upperTick)
  let liquidity
  if (current_tick < lowerTick) {
    liquidity = estimateLiquidityForCoinA(lower_sqrt_price, upper_sqrt_price, coinAmount)
  } else if (current_tick > upperTick) {
    liquidity = estimateLiquidityForCoinB(upper_sqrt_price, lower_sqrt_price, coinAmount)
  } else if (isCoinA) {
    liquidity = estimateLiquidityForCoinA(curSqrtPrice, upper_sqrt_price, coinAmount)
  } else {
    liquidity = estimateLiquidityForCoinB(curSqrtPrice, lower_sqrt_price, coinAmount)
  }
  const coin_amounts = getCoinAmountFromLiquidity(liquidity, curSqrtPrice, lower_sqrt_price, upper_sqrt_price, roundUp)
  const token_limit_a = roundUp
    ? d(coin_amounts.coin_amount_a.toString())
      .mul(1 + slippage)
      .toString()
    : d(coin_amounts.coin_amount_a.toString())
      .mul(1 - slippage)
      .toString()

  const token_limit_b = roundUp
    ? d(coin_amounts.coin_amount_b.toString())
      .mul(1 + slippage)
      .toString()
    : d(coin_amounts.coin_amount_b.toString())
      .mul(1 - slippage)
      .toString()

  return {
    coin_amount_a: coin_amounts.coin_amount_a,
    coin_amount_b: coin_amounts.coin_amount_b,
    coin_amount_limit_a: roundUp ? Decimal.ceil(token_limit_a).toString() : Decimal.floor(token_limit_a).toString(),
    coin_amount_limit_b: roundUp ? Decimal.ceil(token_limit_b).toString() : Decimal.floor(token_limit_b).toString(),
    liquidity_amount: liquidity.toString(),
    is_amount_a: isCoinA,
  }
}

/**
 * Estimate liquidity for coin A
 * @param sqrt_price_x - coin A sqrt price
 * @param sqrt_price_y - coin B sqrt price
 * @param coin_amount - token amount
 * @return
 */
export function estimateLiquidityForCoinA(sqrt_price_x: BN, sqrt_price_y: BN, coin_amount: BN) {
  const lower_sqrt_price_x64 = BN.min(sqrt_price_x, sqrt_price_y)
  const upper_sqrt_price_x64 = BN.max(sqrt_price_x, sqrt_price_y)
  const num = MathUtil.fromX64BN(coin_amount.mul(upper_sqrt_price_x64).mul(lower_sqrt_price_x64))
  const dem = upper_sqrt_price_x64.sub(lower_sqrt_price_x64)
  return !num.isZero() && !dem.isZero() ? num.div(dem) : new BN(0)
}

/**
 * Estimate liquidity for coin B
 * @param sqrt_price_x - coin A sqrt price
 * @param sqrt_price_y - coin B sqrt price
 * @param coin_amount - token amount
 * @return
 */
export function estimateLiquidityForCoinB(sqrt_price_x: BN, sqrt_price_y: BN, coin_amount: BN) {
  const lower_sqrt_price_x64 = BN.min(sqrt_price_x, sqrt_price_y)
  const upper_sqrt_price_x64 = BN.max(sqrt_price_x, sqrt_price_y)
  const delta = upper_sqrt_price_x64.sub(lower_sqrt_price_x64)
  return !delta.isZero() ? coin_amount.shln(64).div(delta) : new BN(0)
}
